import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { fsLogSleep, fsDeleteSleep, fsGetWeekSleep } from '../services/firestoreDb';
import { localDateKey, localDateKeys } from '../utils/dateKey';

const LEGACY_KEY = 'sm_sleep_log';

function storageScope(uid) {
  return uid ? `user_${uid}` : 'guest';
}

function loadLegacyBlob() {
  try { return JSON.parse(localStorage.getItem(LEGACY_KEY) || '{}'); }
  catch { return {}; }
}

function localGetEntry(date, uid = null) {
  const raw = localStorage.getItem(`sm_sleep_${storageScope(uid)}_${date}`);
  if (raw) {
    try { return JSON.parse(raw); } catch { /* fall through to legacy */ }
  }
  if (!uid) {
    const legacy = loadLegacyBlob()[date];
    if (legacy) return legacy;
  }
  return null;
}

function localSetEntry(date, entry, uid = null) {
  localStorage.setItem(`sm_sleep_${storageScope(uid)}_${date}`, JSON.stringify(entry));
}

function localDeleteEntry(date, uid = null) {
  localStorage.removeItem(`sm_sleep_${storageScope(uid)}_${date}`);
}

function localGetWeekData(uid = null) {
  const today = localDateKey();
  return localDateKeys(7).map((date) => {
    const day = new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' });
    const entry = localGetEntry(date, uid);
    return {
      date,
      day,
      hours: entry?.hours ?? 0,
      quality: entry?.quality ?? null,
      isToday: date === today,
    };
  });
}

export function useSleep() {
  const { user } = useAuth();
  const uid = user?.id;

  const [weekData, setWeekData] = useState(() => localGetWeekData(null));
  const writeQueue = useRef(Promise.resolve());

  useEffect(() => {
    let active = true;
    setWeekData(localGetWeekData(uid));

    if (!uid) return () => { active = false; };

    fsGetWeekSleep(uid)
      .then(async (remoteWeek) => {
        if (!active || !remoteWeek) return;
        const hasAnyRemote = remoteWeek.some((d) => d.hours > 0);
        if (!hasAnyRemote) {
          const localWeek = localGetWeekData(uid);
          const toMigrate = localWeek.filter((d) => d.hours > 0);
          if (toMigrate.length) {
            await Promise.all(
              toMigrate.map((d) => fsLogSleep(uid, d.date, d.hours, d.quality).catch(() => {}))
            );
          }
          if (active) setWeekData(toMigrate.length ? localWeek : remoteWeek);
        } else {
          remoteWeek.forEach((d) => localSetEntry(d.date, { hours: d.hours, quality: d.quality }, uid));
          if (active) {
            const today = localDateKey();
            setWeekData(remoteWeek.map((d) => ({ ...d, isToday: d.date === today })));
          }
        }
      })
      .catch(() => {});

    return () => { active = false; };
  }, [uid]);

  const logSleep = useCallback((hours, quality = null, dateStr = null) => {
    const date = dateStr || localDateKey();
    const entry = { hours, quality };
    localSetEntry(date, entry, uid);
    setWeekData((prev) => prev.map((d) => (d.date === date ? { ...d, hours, quality } : d)));
    if (uid) {
      writeQueue.current = writeQueue.current
        .catch(() => {})
        .then(() => fsLogSleep(uid, date, hours, quality));
    }
  }, [uid]);

  const deleteSleep = useCallback((dateStr) => {
    localDeleteEntry(dateStr, uid);
    setWeekData((prev) => prev.map((d) => (d.date === dateStr ? { ...d, hours: 0, quality: null } : d)));
    if (uid) {
      writeQueue.current = writeQueue.current
        .catch(() => {})
        .then(() => fsDeleteSleep(uid, dateStr));
    }
  }, [uid]);

  const todayData = weekData.find((d) => d.isToday) || null;
  const todayEntry = todayData && todayData.hours > 0 ? todayData : null;
  const weekAvg = (() => {
    const filled = weekData.filter((d) => d.hours > 0);
    return filled.length ? filled.reduce((s, d) => s + d.hours, 0) / filled.length : 0;
  })();

  return { weekData, todayEntry, weekAvg, logSleep, deleteSleep };
}
